"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, Send, CircleCheck, Building2, Stethoscope, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function ClinicalDialogueForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="15906" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-chart-4 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-chart-2" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground font-sans">Journey Started</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button variant="outline" className="mt-6 rounded-full border-chart-2 text-chart-2 hover:bg-chart-2 hover:text-white" onClick={() => window.location.reload()}>
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-chart-2 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="job_title" rules={{
        required: "Subject is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Subject</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Spine Consultation" className="border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-chart-2 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="organisation" rules={{
      required: "City / Region is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">City / Region</FormLabel>
            <div className="relative">
              <Building2 className="absolute left-0 top-2.5 size-4 text-chart-2" />
              <FormControl>
                <Input className="pl-7 border-0 border-b border-input rounded-none focus-visible:ring-0 focus-visible:border-chart-2 transition-all" placeholder="Your current location" {...field} />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@example.com" className="border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-chart-2 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-chart-2 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="specialty" rules={{
      required: "Please select a condition area"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Condition Area</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b border-input rounded-none px-0 focus:ring-0 focus:border-chart-2 transition-all">
                  <SelectValue placeholder="Select primary concern" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Brain">Brain Disorders</SelectItem>
                <SelectItem value="Spine">Spine & Back</SelectItem>
                <SelectItem value="Peripheral Nerve">Peripheral Nerve</SelectItem>
                <SelectItem value="Pain Management">Pain Management</SelectItem>
                <SelectItem value="Other">Other Inquiry</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">How can we help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your symptoms or specific questions..." className="min-h-[120px] resize-y border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-chart-2 transition-all" {...field} />
            </FormControl>
            <FormDescription className="italic font-serif">
              We treat every patient with the same compassion as our own family.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="space-y-4">
        <FormField control={control} name="consent_reminders" rules={{
        required: "You must agree to the terms to continue"
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border/50 p-4 bg-muted/20">
              <FormControl>
                <input type="checkbox" className="mt-1 h-4 w-4 rounded border-input text-chart-2 focus:ring-chart-2 accent-chart-2 cursor-pointer" checked={field.value} onChange={e => field.onChange(e.target.checked)} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-[10px] sm:text-xs text-muted-foreground font-normal leading-relaxed cursor-pointer select-none">
                  I agree to receive appointment reminders and consultation updates messages from NeuroFamily, messages are sent via an autodialer, and this agreement isn't a condition of any purchase. I also agree to the <Link to="#" className="text-[#0000EE] hover:underline">Terms of Service</Link> and <Link to="https://app.termly.io/policy-viewer/policy.html?policyUUID=6ff250dc-8d94-4bcd-97b1-7a17f9f72b2b" newTab className="text-[#0000EE] hover:underline">Privacy Policy</Link>. 4 Msgs/Month. Msg & Data Rates may apply. Text STOP to opt out anytime. Text Help for more information.
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-2">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" className="w-full md:w-auto min-w-[220px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-lg shadow-primary/20 transition-all duration-300 hover:scale-[1.02]" disabled={isSubmitting}>
        {isSubmitting ? <>Processing...</> : <>
            Begin Your Journey <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function StartAClinicalDialogue() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="start-your-healing-journey" className="relative py-24 bg-background overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 bg-[radial-gradient(hsl(222_47%_90%)_1px,transparent_1px)] [background-size:32px_32px] opacity-40 pointer-events-none" />
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-chart-4/20 to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-start transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          <div className="space-y-10 pt-4">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-chart-2/10 text-chart-2 text-sm font-medium border border-chart-2/20">
                <Stethoscope className="size-4" />
                <span>Patient-First Care</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-sans">
                Start Your Healing <span className="text-chart-2">Journey</span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                At NeuroFamily, we believe in treating every individual with the same compassion and respect as our own family. Connect with our multidisciplinary team to discuss your path to recovery through advanced, minimally invasive care.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center shrink-0 group-hover:border-chart-2/50 group-hover:shadow-md transition-all duration-300">
                  <Phone className="size-5 text-chart-2" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1 font-sans">Direct Patient Line</h3>
                  <p className="text-muted-foreground text-sm mb-1">Compassionate support available</p>
                  <Link to="tel:+15551234567" className="text-lg font-medium hover:text-chart-2 transition-colors">929-357-7590</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center shrink-0 group-hover:border-chart-2/50 group-hover:shadow-md transition-all duration-300">
                  <Mail className="size-5 text-chart-2" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1 font-sans">Email Consultations</h3>
                  <p className="text-muted-foreground text-sm mb-1">Confidential medical inquiries</p>
                  <Link to="mailto:info@seanarminoncology.com" className="text-lg font-medium hover:text-chart-2 transition-colors">info@seanarminoncology.com</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center shrink-0 group-hover:border-chart-2/50 group-hover:shadow-md transition-all duration-300">
                  <MapPin className="size-5 text-chart-2" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1 font-sans">Our Practice</h3>
                  <p className="text-muted-foreground text-sm max-w-xs">
                    State-of-the-art neurosurgical center designed for comfort and recovery.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center shrink-0 group-hover:border-chart-2/50 group-hover:shadow-md transition-all duration-300">
                  <Clock className="size-5 text-chart-2" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1 font-sans">Patient Hours</h3>
                  <p className="text-muted-foreground text-sm">
                    Monday to Friday, 9:00 AM – 5:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-b from-chart-4/30 to-transparent rounded-2xl blur-md opacity-40" />
            <div className="relative bg-card/60 backdrop-blur-xl border border-border rounded-2xl p-6 md:p-8 shadow-xl">
              <div className="mb-8 pb-6 border-b border-border/50">
                <div className="flex items-center gap-3 mb-2">
                  <MessageSquare className="size-5 text-chart-2" />
                  <h3 className="text-xl font-semibold font-sans">Start Your Healing Journey</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Please share your details below. A member of our clinical family will reach out within one business day.
                </p>
              </div>

              <FormProvider formKey="start_healing_form_connect" formId="start_healing_form_connect" sectionName="start-your-healing-journey" defaultValues={{
              full_name: "",
              job_title: "",
              organisation: "",
              email: "",
              phone: "",
              specialty: "",
              message: "",
              consent_reminders: false
            }} validationRules={{
              full_name: {
                required: true
              },
              job_title: {
                required: true
              },
              organisation: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              specialty: {
                required: true
              },
              message: {
                required: true
              },
              consent_reminders: {
                required: true
              }
            }} submitText="Begin Your Journey" successMessage="Thank you for trusting us with your care. A NeuroFamily specialist will review your inquiry and contact you shortly.">
                <ClinicalDialogueForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}